package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.example.project.service.FrameworkUserSettingsService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

const val POPUP_ENGINE_DEFAULT_WIDTH = 980
const val POPUP_ENGINE_DEFAULT_HEIGHT = 700
const val POPUP_ENGINE_LAYER_Z_INDEX = 9999
const val POPUP_ENGINE_WINDOW_Z_START = 11000
const val POPUP_ENGINE_MODAL_Z_INDEX = 20000
const val POPUP_ENGINE_FLOATING_CHILD_Z_INDEX = 21000

private fun setBodyUserSelect(value: String) {
    document.body?.asDynamic()?.style?.userSelect = value
    document.body?.asDynamic()?.style?.webkitUserSelect = value
}

private data class PopupViewportBounds(
    val x: Double,
    val y: Double,
    val width: Double,
    val height: Double
)

private data class PopupPointerPosition(
    val clientX: Double,
    val clientY: Double
)

private fun popupPointerPosition(event: Event): PopupPointerPosition? {
    val mouseEvent = event as? MouseEvent
    if (mouseEvent != null) {
        return PopupPointerPosition(mouseEvent.clientX.toDouble(), mouseEvent.clientY.toDouble())
    }

    val touchEvent = event as? TouchEvent ?: return null
    val touch = touchEvent.touches.item(0) ?: touchEvent.changedTouches.item(0) ?: return null
    return PopupPointerPosition(touch.clientX.toDouble(), touch.clientY.toDouble())
}

private fun clampPopupBounds(
    x: Double,
    y: Double,
    width: Double,
    height: Double,
    minWidth: Double,
    minHeight: Double,
    minX: Double,
    minY: Double,
    rightInset: Double,
    bottomInset: Double
): PopupViewportBounds {
    val viewportWidth = window.innerWidth.toDouble()
    val viewportHeight = window.innerHeight.toDouble()
    val availableWidth = (viewportWidth - minX - rightInset).coerceAtLeast(minWidth)
    val availableHeight = (viewportHeight - minY - bottomInset).coerceAtLeast(minHeight)
    val clampedWidth = width.coerceIn(minWidth, availableWidth)
    val clampedHeight = height.coerceIn(minHeight, availableHeight)
    val maxX = (viewportWidth - rightInset - clampedWidth).coerceAtLeast(minX)
    val maxY = (viewportHeight - bottomInset - clampedHeight).coerceAtLeast(minY)
    return PopupViewportBounds(
        x = x.coerceIn(minX, maxX),
        y = y.coerceIn(minY, maxY),
        width = clampedWidth,
        height = clampedHeight
    )
}


@Composable
fun PopupEngineHost(
    topBarScreens: List<All>,
    openKeys: List<String>,
    contentInsetLeft: Double = 0.0,
    contentInsetTop: Double = 0.0,
    contentInsetRight: Double = 0.0,
    contentInsetBottom: Double = 0.0,
    onClose: (String) -> Unit
) {
    var nextZ by remember { mutableStateOf(POPUP_ENGINE_WINDOW_Z_START) }
    var zByKey by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    fun bringFront(key: String) {
        nextZ++
        zByKey = zByKey + (key to nextZ)
    }

    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            property("z-index", POPUP_ENGINE_LAYER_Z_INDEX.toString())
            property("pointer-events", "none")
        }
    }) {
        openKeys.forEachIndexed { index, key ->
            val screen = topBarScreens.firstOrNull { it.key == key } ?: return@forEachIndexed
            val z = zByKey[key] ?: (POPUP_ENGINE_WINDOW_Z_START + index)
            PopupEngineCard(
                windowKey = key,
                zIndex = z,
                widthPx = screen.popupWidthPx,
                heightPx = screen.popupHeightPx,
                minWidthPx = screen.popupMinWidthPx,
                minHeightPx = screen.popupMinHeightPx,
                initialX = screen.popupInitialXPx,
                initialY = screen.popupInitialYPx,
                contentInsetLeft = contentInsetLeft,
                contentInsetTop = contentInsetTop,
                contentInsetRight = contentInsetRight,
                contentInsetBottom = contentInsetBottom,
                onActivate = { bringFront(key) },
                onClose = { onClose(key) }
            ) {
                TrackedScreenHost(screen) {
                    screen.content?.invoke(screen)
                }
            }
        }
    }
}

@Composable
fun PopupEngineCard(
    windowKey: String,
    zIndex: Int = 11000,
    widthPx: Int = POPUP_ENGINE_DEFAULT_WIDTH,
    heightPx: Int? = POPUP_ENGINE_DEFAULT_HEIGHT,
    minWidthPx: Int = 720,
    minHeightPx: Int = 520,
    initialX: Double = 170.0,
    initialY: Double = 90.0,
    contentInsetLeft: Double = 0.0,
    contentInsetTop: Double = 0.0,
    contentInsetRight: Double = 0.0,
    contentInsetBottom: Double = 0.0,
    contentBackground: String? = DashboardTheme.POPUP_BG,
    frameBackground: String? = DashboardTheme.POPUP_BG,
    resizable: Boolean = true,
    viewportAnchored: Boolean = false,
    centered: Boolean = false,
    showCloseIcon: Boolean = true,
    allowContentOverflow: Boolean = false,
    onActivate: (() -> Unit)? = null,
    onClose: () -> Unit,
    content: @Composable () -> Unit
) {
    val scope = rememberCoroutineScope()
    val minWidth = minWidthPx.toDouble()
    val minHeight = minHeightPx.toDouble()
    val userSettingsVersion = FrameworkUserSettingsService.currentUserSettingsVersion()
    val clampMinX = 0.0
    val clampMinY = 0.0
    val clampRightInset = 0.0
    val clampBottomInset = 0.0

    val saved = remember(windowKey) { FrameworkUserSettingsService.getPopupSettings(windowKey) }

    var x by remember(windowKey) { mutableStateOf(saved?.getOrNull(0) ?: initialX) }
    var y by remember(windowKey) { mutableStateOf(saved?.getOrNull(1) ?: initialY) }
    var width by remember(windowKey) { mutableStateOf(saved?.getOrNull(2) ?: widthPx.toDouble()) }
    var height by remember(windowKey) { mutableStateOf(saved?.getOrNull(3) ?: (heightPx ?: POPUP_ENGINE_DEFAULT_HEIGHT).toDouble()) }

    LaunchedEffect(windowKey, userSettingsVersion) {
        FrameworkUserSettingsService.ensureCurrentUserSettingsLoaded(forceReload = userSettingsVersion > 0L)
        val loaded = FrameworkUserSettingsService.getPopupSettings(windowKey)
        if (loaded != null) {
            x = loaded.getOrNull(0) ?: x
            y = loaded.getOrNull(1) ?: y
            width = loaded.getOrNull(2) ?: width
            height = loaded.getOrNull(3) ?: height
        }
        val clamped = clampPopupBounds(
            x = x,
            y = y,
            width = width,
            height = height,
            minWidth = minWidth,
            minHeight = minHeight,
            minX = clampMinX,
            minY = clampMinY,
            rightInset = clampRightInset,
            bottomInset = clampBottomInset
        )
        x = clamped.x
        y = clamped.y
        width = clamped.width
        height = clamped.height
    }

    var dragging by remember(windowKey) { mutableStateOf(false) }
    var resizing by remember(windowKey) { mutableStateOf(false) }
    var startX by remember(windowKey) { mutableStateOf(0.0) }
    var startY by remember(windowKey) { mutableStateOf(0.0) }
    var startWidth by remember(windowKey) { mutableStateOf(width) }
    var startHeight by remember(windowKey) { mutableStateOf(height) }

    DisposableEffect(windowKey, dragging, resizing) {
        val move: (Event) -> Unit = move@{ ev ->
            val pointer = popupPointerPosition(ev) ?: return@move
            if (dragging) {
                ev.preventDefault()
                val clamped = clampPopupBounds(
                    x = x + (pointer.clientX - startX),
                    y = y + (pointer.clientY - startY),
                    width = width,
                    height = height,
                    minWidth = minWidth,
                    minHeight = minHeight,
                    minX = clampMinX,
                    minY = clampMinY,
                    rightInset = clampRightInset,
                    bottomInset = clampBottomInset
                )
                x = clamped.x
                y = clamped.y
                startX = pointer.clientX
                startY = pointer.clientY
            } else if (resizing) {
                ev.preventDefault()
                val deltaX = pointer.clientX - startX
                val deltaY = pointer.clientY - startY
                val clamped = clampPopupBounds(
                    x = x,
                    y = y,
                    width = startWidth + deltaX,
                    height = startHeight + deltaY,
                    minWidth = minWidth,
                    minHeight = minHeight,
                    minX = clampMinX,
                    minY = clampMinY,
                    rightInset = clampRightInset,
                    bottomInset = clampBottomInset
                )
                x = clamped.x
                y = clamped.y
                width = clamped.width
                height = clamped.height
            }
        }
        val up: (Event) -> Unit = {
            if (dragging || resizing) {
                scope.launch { FrameworkUserSettingsService.saveCurrentUserPopupSettings(windowKey, x, y, width, height) }
            }
            setBodyUserSelect("")
            dragging = false
            resizing = false
        }
        document.addEventListener("mousemove", move)
        document.addEventListener("mouseup", up)
        document.addEventListener("touchmove", move)
        document.addEventListener("touchend", up)
        document.addEventListener("touchcancel", up)
        onDispose {
            document.removeEventListener("mousemove", move)
            document.removeEventListener("mouseup", up)
            document.removeEventListener("touchmove", move)
            document.removeEventListener("touchend", up)
            document.removeEventListener("touchcancel", up)
        }
    }

    DisposableEffect(windowKey) {
        val resize: (Event) -> Unit = {
            val clamped = clampPopupBounds(
                x = x,
                y = y,
                width = width,
                height = height,
                minWidth = minWidth,
                minHeight = minHeight,
                minX = clampMinX,
                minY = clampMinY,
                rightInset = clampRightInset,
                bottomInset = clampBottomInset
            )
            x = clamped.x
            y = clamped.y
            width = clamped.width
            height = clamped.height
        }
        window.addEventListener("resize", resize)
        onDispose {
            window.removeEventListener("resize", resize)
        }
    }

    LaunchedEffect(windowKey, width, height) {
        window.dispatchEvent(js("new Event('resize')"))
    }

    Div({
        style {
            position(if (viewportAnchored) Position.Fixed else Position.Absolute)
            if (centered) {
                left(50.percent); top(50.percent)
                property("transform", "translate(-50%, -50%)")
                property("max-width", "calc(100vw - 32px)")
            } else {
                left(x.px); top(y.px)
            }
            width(width.px)
            if (heightPx != null) {
                height(height.px)
            } else {
                property("height", "auto")
            }
            borderRadius(12.px)
            if (frameBackground != null) {
                property("background", frameBackground)
            } else {
                property("background", "transparent")
            }
            property("border", "1px solid rgba(255,255,255,0.18)")
            property("box-shadow", "0 24px 70px rgba(0,0,0,0.66), 0 0 30px rgba(235,242,255,0.12), inset 0 1px 0 rgba(255,255,255,0.12)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow(if (allowContentOverflow) "visible" else "hidden")
            property("pointer-events", "auto")
            property("z-index", zIndex.toString())
        }
        onMouseDown {
            it.stopPropagation()
            onActivate?.invoke()
        }
        onClick { it.stopPropagation() }
    }) {
        // Drag handle
        if (!centered) Div({
            style {
                position(Position.Absolute)
                top(0.px)
                left(0.px)
                right(0.px)
                height(28.px)
                backgroundColor(Color.transparent)
                cursor("grab")
                property("z-index", "3")
                property("user-select", "none")
                property("touch-action", "none")
            }
            onMouseDown {
                it.stopPropagation()
                setBodyUserSelect("none")
                dragging = true
                startX = it.clientX.toDouble()
                startY = it.clientY.toDouble()
            }
            onTouchStart {
                it.stopPropagation()
                it.preventDefault()
                val touch = it.touches.item(0) ?: return@onTouchStart
                setBodyUserSelect("none")
                dragging = true
                startX = touch.clientX.toDouble()
                startY = touch.clientY.toDouble()
            }
        })

        if (showCloseIcon) {
            Img(src = "images/miniclose.svg", attrs = {
                onMouseDown {
                    it.stopPropagation()
                }
                onTouchStart {
                    it.stopPropagation()
                }
                onClick {
                    it.stopPropagation()
                    onClose()
                }
                style {
                    position(Position.Absolute)
                    top(10.px)
                    right(10.px)
                    width(16.px)
                    height(16.px)
                    cursor("pointer")
                    property("filter", "brightness(0) invert(1)")
                    property("z-index", "6")
                }
            })
        }

        Div({
            style {
                flexGrow(1)
                minHeight(0.px)
                overflow(if (allowContentOverflow) "visible" else "hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color.transparent)
                if (contentBackground != null) {
                    property("background", contentBackground)
                } else {
                    property("background", "transparent")
                }
            }
        }) {
            content()
        }

        if (resizable) {
            Div({
                style {
                    position(Position.Absolute)
                    right(0.px)
                    bottom(0.px)
                    width(26.px)
                    height(26.px)
                    cursor("nwse-resize")
                    property(
                        "background",
                        "linear-gradient(135deg, transparent 0 48%, rgba(255,255,255,0.00) 48% 56%, rgba(255,255,255,0.24) 56% 60%, transparent 60% 68%, rgba(255,255,255,0.24) 68% 72%, transparent 72% 80%, rgba(255,255,255,0.24) 80% 84%, transparent 84% 100%)"
                    )
                    property("border-top-left-radius", "10px")
                    property("user-select", "none")
                    property("touch-action", "none")
                    property("z-index", "5")
                }
                onMouseDown {
                    it.stopPropagation()
                    onActivate?.invoke()
                    setBodyUserSelect("none")
                    resizing = true
                    startX = it.clientX.toDouble()
                    startY = it.clientY.toDouble()
                    startWidth = width
                    startHeight = height
                }
                onTouchStart {
                    it.stopPropagation()
                    it.preventDefault()
                    val touch = it.touches.item(0) ?: return@onTouchStart
                    onActivate?.invoke()
                    setBodyUserSelect("none")
                    resizing = true
                    startX = touch.clientX.toDouble()
                    startY = touch.clientY.toDouble()
                    startWidth = width
                    startHeight = height
                }
            })
        }
    }
}
